"use client";
import { FC, useState } from "react";
import { ALL_IPO, IpoListSubType, MAINBOARD, SME } from "./IpoListConstants";
import { getIpoListData } from "../../services/TradingMallService";
import IpoListItem from "./IpoListItem";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import Conditional from "@repo/shared-ui/Conditional";
import {
    IIpoListWrapper,
    IIpoListAPIResponse,
    IIpoListTabPanelState,
} from "../../interfaces/IpoList.interface"
import { componentNameConstants, dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { IpoListFunctions } from "./IpoListFunctions";
import { getFullUrl, getAbsoluteUrl } from "../../utils/helper";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";
import IpoListNotFound from "./IpoListNotFound";
import Link from "next/link";

const LoaderCards = () => {
    return (
        <div className="flex flex-col gap-3 w-full max-w-[892px]">
            {Array.from({ length: 5 }).map((_, idx) => (
                <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`ipo-list-skeleton-${idx}`}
                    className="w-full min-h-[112px] border border-[#E5EAF7] rounded-[12px] p-4 flex gap-3"
                >
                    <BFLSkeleton height={48} width={48} classes="rounded-[8px]" />
                    <div className="flex flex-col gap-2 flex-1">
                        <BFLSkeleton height={16} width="85%" classes="rounded-[6px]" />
                        <BFLSkeleton height={16} width="60%" classes="rounded-[6px]" />
                        <BFLSkeleton height={14} width="40%" classes="rounded-[6px]" />
                    </div>
                </div>
            ))}
        </div>
    );
};

const createInitialTabState = (
    mainboardInitial: IIpoListAPIResponse,
    smeInitial: IIpoListAPIResponse,
): Record<IpoListSubType, IIpoListTabPanelState> => ({
    [MAINBOARD]: {
        ipoList: mainboardInitial.data?.ipoDetails ?? [],
        page: 1,
        showMore: mainboardInitial.data?.showmore ?? false,
        loading: false,
    },
    [SME]: {
        ipoList: smeInitial.data?.ipoDetails ?? [],
        page: 1,
        showMore: smeInitial.data?.showmore ?? false,
        loading: false,
    },
});

const IpoListWrapper: FC<IIpoListWrapper> = ({
    mainboardInitial,
    smeInitial,
    ipoType,
    biddingPeriodText,
    minimumInvestmentText,
    bidPriceText,
    listingDateText,
    listingGainText,
    tbaText,
    showMoreCtaText,
    paginationLimit,
    mainboardTitle,
    smeTitle,
    pageType,
    viewAllText,
    viewAllOpenNewTab,
    viewAllRedirectUrl,
    ipoTitle,
    apiErrorDesc,
    fetchApiDataErrorDesc,
    componentLink,
    openInNewTab
}) => {
    const parsedPaginationLimit = Number.parseInt(paginationLimit ?? "3");

    const hasMainboardData = (mainboardInitial.data?.ipoDetails?.length ?? 0) > 0;
    const hasSmeData = (smeInitial.data?.ipoDetails?.length ?? 0) > 0;
    const showMainboardPanel = hasMainboardData;
    const showSmePanel = hasSmeData;
    const showFallbackPanel = !hasMainboardData && !hasSmeData;
    const defaultSubType = hasMainboardData ? MAINBOARD : SME;
    const ipoDataByType: Record<IpoListSubType, IIpoListAPIResponse> = {
        [MAINBOARD]: mainboardInitial,
        [SME]: smeInitial,
    };
    const getApiSuccess = (type: IpoListSubType) => ipoDataByType[type].statusCode === 90;

    const [subType, setSubType] = useState<IpoListSubType>(defaultSubType);
    const [tabState, setTabState] = useState<Record<IpoListSubType, IIpoListTabPanelState>>(() =>
        createInitialTabState(mainboardInitial, smeInitial),
    );
    const loginStatus = useAppSelector(selectLoginStatus);

    const { handleViewAll } = IpoListFunctions();

    const handleTabSwitch = (type: IpoListSubType) => {
        setSubType(type);
        const dataLayerObj = {
            component: componentNameConstants.IPO_LIST,
            element: dlEventConstants.TAB_CLICK,
            event_type: dlEventConstants.TAB_CLICK,
            event: dlEventConstants.CLICK,
            section_title: type,
            cta_text: type,
        };
        const clevertapObj = {
            EP_PAGE_NAME: ipoTitle,
            EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_IPO_PLP,
            EP_SECTION: ipoType,
            EP_TAB: type,
            EP_CTA: type,
        };
        dataLayer("", dataLayerObj, clevertapObj);

        const mainboardNudge = document.querySelector(`.${componentLink} .ipo-title-with-description__content .mainboard-nudge-value`);
        const smeNudge = document.querySelector(`.${componentLink} .ipo-title-with-description__content .sme-nudge-value`);
        if (type === MAINBOARD) {
            if (mainboardNudge) mainboardNudge.classList.remove("hidden");
            if (smeNudge) smeNudge.classList.add("hidden");
        }
        else {
            if (mainboardNudge) mainboardNudge.classList.add("hidden");
            if (smeNudge) smeNudge.classList.remove("hidden");
        }
    };

    const handleShowMore = async (type: IpoListSubType) => {
        const currentTab = tabState[type];
        setTabState((prev) => ({
            ...prev,
            [type]: { ...prev[type], loading: true },
        }));
        try {
            const response = await getIpoListData({
                ipoType: ipoType.toLowerCase(),
                limit: parsedPaginationLimit,
                page: currentTab.page + 1,
                subType: type,
            });
            if (response?.statusCode === 90) {
                const nextPage = currentTab.page + 1;
                const dataLayerObj = {
                    component: componentNameConstants.IPO_LIST,
                    element: dlEventConstants.BUTTON_CLICK,
                    event_type: dlEventConstants.BUTTON_CLICK,
                    event: dlEventConstants.CLICK,
                    section_title: showMoreCtaText,
                    cta_text: showMoreCtaText,
                };
                const clevertapObj = {
                    EP_PAGE_NAME: ipoTitle,
                    EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_IPO_PLP,
                    EP_SECTION: ipoType,
                    EP_TAB: type,
                    EP_CTA: showMoreCtaText,
                    EP_SHOW_MORE_CLICKED_COUNT: String(nextPage),
                };
                dataLayer("", dataLayerObj, clevertapObj);
                setTabState((prev) => ({
                    ...prev,
                    [type]: {
                        ...prev[type],
                        ipoList: [...prev[type].ipoList, ...(response.data?.ipoDetails ?? [])],
                        page: nextPage,
                        showMore: response.data?.showmore ?? false,
                        loading: false,
                    },
                }));
            } else {
                setTabState((prev) => ({
                    ...prev,
                    [type]: { ...prev[type], loading: false },
                }));
            }
        } catch {
            setTabState((prev) => ({
                ...prev,
                [type]: { ...prev[type], loading: false },
            }));
        }
    };

    const shouldOpenInNewTab = openInNewTab === true || openInNewTab === "true";
    const linkTarget = shouldOpenInNewTab ? "_blank" : "_self";
    const linkRel = shouldOpenInNewTab ? "noopener noreferrer" : undefined;

    const renderTabPanel = (type: IpoListSubType) => {
        const { ipoList, showMore, loading } = tabState[type];
        const apiSuccess = getApiSuccess(type);
        const isActive = subType === type;
        const hasMultiplePanels = hasMainboardData && hasSmeData;

        return (
            <div
                key={type}
                data-ipo-panel={type}
                className={hasMultiplePanels && !isActive ? "hidden" : undefined}
                aria-hidden={hasMultiplePanels ? !isActive : undefined}
            >
                <Conditional if={apiSuccess && ipoList.length > 0}>
                    <div className="flex flex-col gap-3 max-w-[892px] pb-3">
                        {ipoList.map((item, index) => (
                            <Link
                                key={`ipo-${type}-${item.redirectURL}-${index}`}
                                href={getFullUrl(getAbsoluteUrl(`/${(item.redirectURL ?? "").replace(/^\//, "")}`))}  
                                title={item?.name}
                                target={linkTarget}
                                rel={linkRel}
                                className="all-ipo-list-card flex flex-col"
                                tabIndex={isActive ? 0 : -1}
                                onClick={() => {
                                    const dataLayerObj = {
                                        component: componentNameConstants.IPO_LIST,
                                        element: dlEventConstants.CARD_CLICK,
                                        event_type: dlEventConstants.CARD_CLICK,
                                        event: dlEventConstants.CLICK,
                                        section_title: item?.name,
                                        cta_text: item?.name,
                                    };
                                    const clevertapObj = {
                                        EP_PAGE_NAME: pageType,
                                        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_IPO_PLP,
                                        EP_SECTION: ipoType,
                                        EP_TAB: type,
                                        EP_CTA: item?.name,
                                        EP_TILE_CLICKED: item?.name,
                                    };
                                    dataLayer("", dataLayerObj, clevertapObj);
                                }}
                            >
                                <IpoListItem
                                    itemDetails={item}
                                    ipoType={ipoType}
                                    biddingPeriodText={biddingPeriodText}
                                    minimumInvestmentText={minimumInvestmentText}
                                    listingDateText={listingDateText}
                                    listingGainText={listingGainText}
                                    bidPriceText={bidPriceText}
                                    tbaText={tbaText}
                                />
                            </Link>
                        ))}

                        {loading && <LoaderCards />}

                        <Conditional if={!loading && pageType?.toLowerCase() !== ALL_IPO && showMore}>
                            <div className="flex justify-center items-center pb-6">
                                <button
                                    type="button"
                                    className="text-[#ff6700] bg-white cursor-pointer rounded-full border border-[#ff8900] p-[10px] lg:px-[60px] lg:py-[10px] font-medium
                                    text-sm hover:bg-gradient-to-r hover:from-[#ff6700] hover:to-[#ff8900] hover:text-[#feeadd] focus:outline-none w-100 md:w-[200px] whitespace-nowrap"
                                    tabIndex={isActive ? 0 : -1}
                                    onClick={() => handleShowMore(type)}
                                >
                                    {showMoreCtaText}
                                </button>
                            </div>
                        </Conditional>

                        <Conditional if={pageType?.toLowerCase() === ALL_IPO && ipoList.length >= 3}>
                            <button
                                tabIndex={isActive ? 0 : -1}
                                className="cursor-pointer text-[#ff6700] text-sm font-medium text-left"
                                onClick={() =>
                                    handleViewAll(
                                        loginStatus,
                                        viewAllRedirectUrl,
                                        viewAllOpenNewTab,
                                        viewAllText,
                                        ipoTitle
                                    )
                                }
                            >
                                {viewAllText}
                            </button>
                        </Conditional>
                    </div>
                </Conditional>

                <Conditional if={apiSuccess && ipoList.length === 0}>
                    <IpoListNotFound errorMessage={apiErrorDesc} fetchError={false} />
                </Conditional>

                <Conditional if={!apiSuccess}>
                    <IpoListNotFound errorMessage={fetchApiDataErrorDesc} fetchError={true} />
                </Conditional>
            </div>
        );
    };

    return (
        <>
            <div className="all-ipo-list-types flex gap-2">
                <Conditional if={hasMainboardData}>
                    <button
                        tabIndex={0}
                        className={`cursor-pointer border border-[#002953] rounded-md h-[30px] px-2 text-sm flex items-center ${subType === "mainboard" ? "bg-[#002953] text-white" : "text-[#002953]"
                            }`}
                        onClick={() => handleTabSwitch(MAINBOARD)}
                    >
                        {mainboardTitle}
                    </button>
                </Conditional>
                <Conditional if={hasSmeData}>
                    <button
                        tabIndex={0}
                        className={`cursor-pointer border border-[#002953] rounded-md h-[30px] px-2 text-sm flex items-center ${subType === "sme" ? "bg-[#002953] text-white" : "text-[#002953]"
                            }`}
                        onClick={() => handleTabSwitch(SME)}
                    >
                        {smeTitle}
                    </button>
                </Conditional>
            </div>

            {showMainboardPanel && renderTabPanel(MAINBOARD)}
            {showSmePanel && renderTabPanel(SME)}
            {showFallbackPanel && renderTabPanel(defaultSubType)}
        </>
    );
};

export default IpoListWrapper;
